import { db } from './firebase';
import { doc, setDoc, getDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { PokemonCard, Deck } from '../types';

export interface WishlistItem {
  cardId: string;
  cardName: string;
  cardImage: string;
  addedAt: number;
  notes?: string;
}

export interface UserCollection {
  userId: string;
  favorites: PokemonCard[];
  decks: Deck[];
  wishlist: WishlistItem[];
  updatedAt: number;
}

export const collectionService = {
  // Save user's vault (favorites) to Firestore
  async saveVault(userId: string, favorites: PokemonCard[]): Promise<void> {
    if (!db || !userId) return;

    try {
      const docRef = doc(db, 'collections', userId);
      await setDoc(docRef, {
        userId,
        favorites,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.warn('Failed to sync vault to cloud:', error);
      // Silently fail - localStorage backup exists
    }
  },

  // Save user's decks to Firestore
  async saveDecks(userId: string, decks: Deck[]): Promise<void> {
    if (!db || !userId) return;

    try {
      const docRef = doc(db, 'collections', userId);
      await setDoc(docRef, {
        userId,
        decks,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.warn('Failed to sync decks to cloud:', error);
    }
  },

  // Save user's wishlist to Firestore
  async saveWishlist(userId: string, wishlist: WishlistItem[]): Promise<void> {
    if (!db || !userId) return;

    try {
      const docRef = doc(db, 'collections', userId);
      await setDoc(docRef, {
        userId,
        wishlist,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.warn('Failed to sync wishlist to cloud:', error);
    }
  },

  // Load user's collection from Firestore
  async loadCollection(userId: string): Promise<{ favorites: PokemonCard[], decks: Deck[], wishlist: WishlistItem[] } | null> {
    if (!db || !userId) return null;

    try {
      const docRef = doc(db, 'collections', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          favorites: data.favorites || [],
          decks: data.decks || [],
          wishlist: data.wishlist || []
        };
      }
      return null;
    } catch (error) {
      console.warn('Failed to load collection from cloud:', error);
      return null;
    }
  },

  // Subscribe to real-time updates for a user's collection
  subscribeToCollection(
    userId: string,
    onUpdate: (data: { favorites: PokemonCard[], decks: Deck[], wishlist: WishlistItem[] }) => void
  ): () => void {
    if (!db || !userId) return () => {};

    const docRef = doc(db, 'collections', userId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        onUpdate({
          favorites: data.favorites || [],
          decks: data.decks || [],
          wishlist: data.wishlist || []
        });
      }
    }, (error) => {
      console.warn('Collection sync error:', error);
    });

    return unsubscribe;
  },

  // Get another user's public collection (for social features)
  async getPublicCollection(userId: string): Promise<PokemonCard[] | null> {
    if (!db || !userId) return null;

    try {
      // First check if user is public
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists() || !userDoc.data()?.isPublic) {
        return null; // User is private
      }

      const collectionDoc = await getDoc(doc(db, 'collections', userId));
      if (collectionDoc.exists()) {
        return collectionDoc.data().favorites || [];
      }
      return null;
    } catch (error) {
      console.warn('Failed to fetch public collection:', error);
      return null;
    }
  },

  // Get all public collections for leaderboard/social
  async getPublicCollections(): Promise<{ userId: string, favorites: PokemonCard[] }[]> {
    if (!db) return [];

    try {
      // Get all public users
      const usersQuery = query(collection(db, 'users'), where('isPublic', '==', true));
      const usersSnap = await getDocs(usersQuery);

      const publicUserIds = usersSnap.docs.map(d => d.id);

      // Fetch their collections
      const collections: { userId: string, favorites: PokemonCard[] }[] = [];

      for (const userId of publicUserIds) {
        const collDoc = await getDoc(doc(db, 'collections', userId));
        if (collDoc.exists()) {
          collections.push({
            userId,
            favorites: collDoc.data().favorites || []
          });
        }
      }

      return collections;
    } catch (error) {
      console.warn('Failed to fetch public collections:', error);
      return [];
    }
  },

  // Get users who have a specific card on their wishlist
  async getWishlistersForCard(cardId: string): Promise<{ userId: string, userName?: string }[]> {
    if (!db) return [];

    try {
      // Get all public users
      const usersQuery = query(collection(db, 'users'), where('isPublic', '==', true));
      const usersSnap = await getDocs(usersQuery);

      const wishlisters: { userId: string, userName?: string }[] = [];

      for (const userDoc of usersSnap.docs) {
        const collDoc = await getDoc(doc(db, 'collections', userDoc.id));
        if (collDoc.exists()) {
          const wishlist = collDoc.data().wishlist || [];
          const wantsCard = wishlist.some((item: WishlistItem) => item.cardId === cardId);
          if (wantsCard) {
            wishlisters.push({
              userId: userDoc.id,
              userName: userDoc.data().fullName || userDoc.data().name
            });
          }
        }
      }

      return wishlisters;
    } catch (error) {
      console.warn('Failed to fetch wishlisters:', error);
      return [];
    }
  },

  // Check if current user's vault cards are wanted by others (returns matches)
  async getWantedCardsMatches(
    myVault: PokemonCard[],
    myUserId: string
  ): Promise<{ card: PokemonCard, wantedBy: { userId: string, userName?: string }[] }[]> {
    if (!db || myVault.length === 0) return [];

    try {
      // Get all public users except self
      const usersQuery = query(collection(db, 'users'), where('isPublic', '==', true));
      const usersSnap = await getDocs(usersQuery);

      const matches: Map<string, { card: PokemonCard, wantedBy: { userId: string, userName?: string }[] }> = new Map();

      for (const userDoc of usersSnap.docs) {
        if (userDoc.id === myUserId) continue; // Skip self

        const collDoc = await getDoc(doc(db, 'collections', userDoc.id));
        if (collDoc.exists()) {
          const wishlist: WishlistItem[] = collDoc.data().wishlist || [];

          for (const wishItem of wishlist) {
            const matchedCard = myVault.find(c => c.id === wishItem.cardId);
            if (matchedCard) {
              const existing = matches.get(matchedCard.id);
              const wanter = { userId: userDoc.id, userName: userDoc.data().fullName || userDoc.data().name };

              if (existing) {
                existing.wantedBy.push(wanter);
              } else {
                matches.set(matchedCard.id, { card: matchedCard, wantedBy: [wanter] });
              }
            }
          }
        }
      }

      return Array.from(matches.values());
    } catch (error) {
      console.warn('Failed to get wanted cards matches:', error);
      return [];
    }
  }
};
